import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import {
  useExportToCsvMutation,
  useCheckExportStatusQuery,
  useInterruptExportMutation,
  useDeleteCsvFileMutation,
} from 'api/exports'

import IconLink from 'components/Shared/IconLink/Index'
import ExportFileProgressBar from 'components/Shared/ExportFileProgressBar/Index'

const StyledExportDiv = styled.div`
  display: flex;
  align-items: center;
  height: 24px;
`

const DownloadLink = styled.a`
  font-size: 14px;
  margin-left: 16px;
  font-weight: 600;
`
const ExportCsv = ({ type, recordCount, exportingRecordId, isFetching, filterParams, needRemoveCsvFile, onCsvRemoving }) => {
  const [disableExportLink, setDisableExportLink] = useState(false)
  const [displayDownloadLink, setDisplayDownloadLink] = useState(true)
  const [fileGenerating, setFileGenerating] = useState(false)
  const [exportProgress, setExportProgress] = useState(0)
  const [csvFileUrl, setCsvFileUrl] = useState(null)
  const [currentExportingRecordId, setCurrentExportingRecordId] = useState(exportingRecordId)

  const { t } = useTranslation()

  const { data: exportStatus, isFetching: isExportStatusFetching } =
    useCheckExportStatusQuery(
      {
        id: currentExportingRecordId,
        type: type,
      }, {
        pollingInterval: 5000,
        skip: !fileGenerating,
      }
    )

  const [interruptExport] = useInterruptExportMutation()
  const [deleteCsvFile] = useDeleteCsvFileMutation()
  const [exportToCsv, { data: dataCreate, isLoading: isCreating }] = useExportToCsvMutation()

  // in these 2 effects result from the latest API request
  // (either fetch or check_export_status) is used
  // to set fileUrl and fileGenerating parameters
  useEffect(() => {
    if (isFetching) {
      return
    }

    setFileGenerating(!!exportingRecordId)
    setCurrentExportingRecordId(exportingRecordId)
  }, [isFetching]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isExportStatusFetching) {
      return
    }

    const isFileGenerating = exportStatus?.state && (exportStatus?.state === 'processing')

    setFileGenerating(!!isFileGenerating)
    setCsvFileUrl(exportStatus?.fileUrl)
    setExportProgress(exportStatus?.exportProgress || 0)
  }, [isExportStatusFetching, exportStatus])

  // delete CSV file when filter is changed
  useEffect(() => {
    if (needRemoveCsvFile && (csvFileUrl || fileGenerating)) {
      onCsvRemoving()
      deleteCsvFile(type)
      setFileGenerating(false)
      setCsvFileUrl(null)
      setDisplayDownloadLink(false)
      setExportProgress(0)
    }
  }, [filterParams, onCsvRemoving]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isCreating === false && dataCreate) {
      setCurrentExportingRecordId(dataCreate.exportRecordId)
    }
  }, [isCreating, dataCreate])

  const onExportClick = async () => {
    setDisableExportLink(true)

    await exportToCsv({ type: type, filters: filterParams })

    setFileGenerating(true)
    setDisplayDownloadLink(true)
    setDisableExportLink(false)
  }

  const onExportInterrupt = () => {
    interruptExport({ id: currentExportingRecordId, type: type })
    setFileGenerating(false)
    setCsvFileUrl(null)
    setExportProgress(0)
  }

  const onDownloadLinkClick = () => {
    setDisplayDownloadLink(false)
  }

  if (!recordCount) {
    return null
  }

  return (
    <StyledExportDiv>
      {fileGenerating
        ? (
          <ExportFileProgressBar
            currentPercent={exportProgress}
            onAbort={onExportInterrupt}
          />
          )
        : (
          <>
            <IconLink
              onClick={onExportClick}
              iconName='icon-export-to-csv'
              text={t('common.exportToCsv')}
              disabled={disableExportLink}
            />
            {csvFileUrl && displayDownloadLink && (
              <DownloadLink href={csvFileUrl} onClick={onDownloadLinkClick}>
                {t('common.downloadCsv')}
              </DownloadLink>
            )}
          </>
          )
      }
    </StyledExportDiv>
  )
}

ExportCsv.propTypes = {
  type: PropTypes.string.isRequired,
  recordCount: PropTypes.number.isRequired,
  exportingRecordId: PropTypes.number,
  isFetching: PropTypes.bool.isRequired,
  filterParams: PropTypes.object,
  needRemoveCsvFile: PropTypes.bool.isRequired,
  onCsvRemoving: PropTypes.func,
}

export default ExportCsv
